import React, { ReactNode, useEffect } from 'react'
import { useRouter } from 'next/router'
import Cookies from 'js-cookie'
import { BarChart2, ShoppingBag, User, MessageCircle, Icon } from 'react-feather'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useUser } from 'src/lib/contexts/UserContext'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { hasAuthCookie, hasRefreshCookie, setVersionCookie, refresh } from 'src/lib/utils/helpers'
import { refreshExpiredIdToken } from 'src/lib/utils/auth'
import { constants } from 'src/lib/utils/constants'
import { getXShopfloVersion } from 'src/lib/core/api-methods'
import ThirdPartyInitializer from 'src/components/common/initializers/ThirdPartyInitializer'

interface MobileLayoutProps {
  children: ReactNode
}

interface NavItem {
  id: string
  label: string
  path: string
  icon: Icon
}

const navItems: NavItem[] = [
  {
    id: 'analytics',
    label: 'Analytics',
    path: '/mobile',
    icon: BarChart2
  },
  {
    id: 'orders',
    label: 'Orders',
    path: '/mobile/orders',
    icon: ShoppingBag
  },
  {
    id: 'profile',
    label: 'Profile',
    path: '/mobile/profile',
    icon: User
  }
]

/**
 * MobileLayout - Layout wrapper for mobile-specific pages
 * This layout is used for all pages under the /mobile route
 */
const MobileLayout: React.FC<MobileLayoutProps> = ({ children }) => {
  const router = useRouter()
  const { mutateUser } = useUser()
  const {
    getters: { organisationData },
    actions: { refetchOrganisation }
  } = useOrganisationContext()

  // Check for refresh token and handle auth
  const checkForRefreshToken = async () => {
    if (hasRefreshCookie()) {
      await refreshExpiredIdToken()
      onInit()
    } else {
      router.replace('/auth/login')
    }
  }

  // Initialize app - check version and fetch user/org data
  const onInit = async () => {
    try {
      const versionResponse: any = await getXShopfloVersion()
      if (versionResponse?.data?.success) {
        if (Cookies.get(constants.FLO_SHOPFLO_VERSION) !== versionResponse?.data?.data?.version) {
          setVersionCookie(versionResponse?.data?.data?.version ?? 'stable')
          refresh()
        }
      } else {
        setVersionCookie('stable')
      }
      refetchOrganisation()
      mutateUser()
    } catch (error) {
      console.error('Error during initialization:', error)
    }
  }

  // Auth check on mount
  useEffect(() => {
    if (hasAuthCookie()) {
      onInit()
      return
    }
    checkForRefreshToken()
  }, [])

  const isActiveRoute = (path: string): boolean => {
    if (path === '/mobile') {
      return router.pathname === '/mobile'
    }
    return router.pathname.startsWith(path)
  }

  const handleNavigation = (path: string) => {
    router.push(path)
  }

  const handleHelpClick = () => {
    // Open Intercom messenger
    if (typeof window !== 'undefined' && (window as any).Intercom) {
      ;(window as any).Intercom('show')
    }
  }

  return (
    <>
      <div className='flex flex-col h-screen bg-white overflow-hidden'>
        {/* Main Content - with padding bottom for fixed navbar */}
        <main className='flex-1 overflow-y-auto pb-20'>{children}</main>

        {/* Mobile Bottom Navigation */}
        <nav className='fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 z-50'>
          <div className='flex items-center justify-around py-2 px-2'>
            {navItems.map((item, index) => {
              const isActive = isActiveRoute(item.path)
              const Icon = item.icon

              return (
                <React.Fragment key={item.id}>
                  <button
                    onClick={() => handleNavigation(item.path)}
                    className='flex flex-col items-center justify-center py-2 px-3 min-w-[72px] rounded-lg transition-colors'
                  >
                    <Icon size={22} className={isActive ? 'text-emerald-500' : 'text-zinc-400'} />
                    <span className={`text-xs mt-1 font-medium ${isActive ? 'text-emerald-500' : 'text-zinc-500'}`}>
                      {item.label}
                    </span>
                  </button>
                  {/* Insert Help button between Orders (index 1) and Profile (index 2) */}
                  {index === 1 && (
                    <button
                      id='mobile-intercom-button'
                      onClick={handleHelpClick}
                      className='flex flex-col items-center justify-center py-2 px-3 min-w-[72px] rounded-lg transition-colors'
                    >
                      <MessageCircle size={22} className='text-zinc-400' />
                      <span className='text-xs mt-1 font-medium text-zinc-500'>Help</span>
                    </button>
                  )}
                </React.Fragment>
              )
            })}
          </div>
          {/* Safe area for devices with home indicator */}
          <div className='h-safe-area-inset-bottom bg-white' />
        </nav>
      </div>
      {/* Third Party Initializer - Intercom, ProductFruits, Clarity */}
      <ThirdPartyInitializer />
    </>
  )
}

export default MobileLayout
